import React, { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import ConversationList from '../../components/AIGenerate/ConversationList';
import ChatArea from '../../components/AIGenerate/ChatArea';
import InputPanel from '../../components/AIGenerate/InputPanel';
import EffectPanel from '../../components/AIGenerate/EffectPanel';
import type {
  AiGenerateCapability,
  Conversation,
  EffectTemplate,
  GenerateParams,
  InputImageRecord,
  Message,
} from '../../types/aiGenerate';
import * as aiService from '../../services/aiGenerate';
import { useTranslation } from '../../hooks/useTranslation';
import './index.css';

// 提取友好的错误信息，避免显示原始JSON响应
const extractFriendlyError = (errorMessage: string | undefined, t: (key: string) => string): string => {
  if (!errorMessage) {
    return t('aiGenerate.unknownError');
  }
  if (errorMessage.startsWith('aiGenerate.')) {
    return t(errorMessage);
  }
  try {
    const jsonMatch = errorMessage.match(/\{"code":\d+.*?"message":"([^"]+)"/);
    if (jsonMatch && jsonMatch[1]) {
      return jsonMatch[1];
    }
  } catch (e) {
    // ignore
  }
  if (errorMessage.length > 150) {
    return errorMessage.substring(0, 150) + '...';
  }
  return errorMessage;
};

const AIGenerate: React.FC = () => {
  const { t } = useTranslation();
  // 对话列表
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  // 消息列表
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [capability, setCapability] = useState<AiGenerateCapability | null>(null);

  // 输入状态
  const [inputImages, setInputImages] = useState<InputImageRecord[]>([]);
  const [inputText, setInputText] = useState('');
  const [generateParams, setGenerateParams] = useState<GenerateParams>(
    aiService.DEFAULT_GENERATE_PARAMS
  );

  // 生成状态
  const [generating, setGenerating] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // 标记是否跳过下一次 loadMessages（用于新建对话后立即发送消息的场景）
  const skipLoadMessagesRef = useRef(false);

  // 效果模板
  const [templates, setTemplates] = useState<EffectTemplate[]>([]);
  const [showCompare, setShowCompare] = useState(false);

  // 加载对话列表
  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const data = await aiService.getConversations();
      setConversations(data);
    } catch (error: any) {
      console.error('加载对话列表失败:', error);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  // 加载消息
  const loadMessages = useCallback(async (conversationId: string) => {
    setMessagesLoading(true);
    try {
      const data = await aiService.getMessages(conversationId);
      setMessages(data);
    } catch (error: any) {
      console.error('加载消息失败:', error);
      message.error(t('aiGenerate.loadMessagesFailed'));
    } finally {
      setMessagesLoading(false);
    }
  }, [t]);

  // 加载模板
  const loadTemplates = useCallback(async () => {
    try {
      const data = await aiService.getEffectTemplates();
      setTemplates(data);
    } catch (error: any) {
      console.error('加载模板失败:', error);
    }
  }, []);

  const loadCapability = useCallback(async () => {
    try {
      const data = await aiService.getCapability();
      setCapability(data);
      setGenerateParams((current) => ({ ...current, model: data.model || current.model }));
    } catch (error) {
      console.error('加载 AI 生图能力失败:', error);
      setCapability(null);
    }
  }, []);

  // 初始化
  useEffect(() => {
    loadConversations();
    loadTemplates();
    loadCapability();
  }, [loadCapability, loadConversations, loadTemplates]);

  // 隐藏全局 footer
  useEffect(() => {
    const footer = document.querySelector('.home-cover-footer') as HTMLElement;
    if (footer) {
      footer.style.display = 'none';
    }
    return () => {
      if (footer) {
        footer.style.display = '';
      }
    };
  }, []);

  // 切换对话时加载消息
  useEffect(() => {
    // 如果标记了跳过，则不加载消息（用于新建对话后立即发送消息的场景）
    if (skipLoadMessagesRef.current) {
      skipLoadMessagesRef.current = false;
      return;
    }
    if (currentConversationId) {
      loadMessages(currentConversationId);
    } else {
      setMessages([]);
    }
  }, [currentConversationId, loadMessages]);

  // 创建新对话
  const handleCreateConversation = async () => {
    try {
      const newConversation = await aiService.createConversation();
      setConversations([newConversation, ...conversations]);
      setCurrentConversationId(newConversation.id);
      setMessages([]);
      setInputImages([]);
      setInputText('');
    } catch (error: any) {
      message.error(error.message || '创建对话失败');
    }
  };

  // 选择对话
  const handleSelectConversation = (id: string) => {
    if (generating) {
      message.warning(t('aiGenerate.generatingSwitchHint'));
      return;
    }
    setCurrentConversationId(id);
    setInputImages([]);
    setInputText('');
  };

  // 删除对话
  const handleDeleteConversation = async (id: string) => {
    try {
      await aiService.deleteConversation(id);
      setConversations(conversations.filter((c) => c.id !== id));
      if (currentConversationId === id) {
        setCurrentConversationId(null);
        setMessages([]);
      }
      message.success(t('aiGenerate.deleteSuccess'));
    } catch (error: any) {
      message.error(error.message || t('aiGenerate.deleteFailed'));
    }
  };

  // 重命名对话
  const handleRenameConversation = async (id: string, title: string) => {
    try {
      await aiService.updateConversation(id, title);
      setConversations(
        conversations.map((c) => (c.id === id ? { ...c, title } : c))
      );
    } catch (error: any) {
      message.error(error.message || t('aiGenerate.renameFailed'));
    }
  };

  // 轮询任务状态
  // fallbackMessageId 用于处理竞态条件：当 setMessages 还未完成时，消息 ID 可能还是原始值
  const pollTaskStatus = useCallback(
    async (taskId: string, messageId: string, fallbackMessageId?: string) => {
      try {
        const status = await aiService.getTaskStatus(taskId);

        setMessages((prev) => {
          let changed = false;
          const next = prev.map((msg) => {
            // 同时匹配新 ID 和原始 ID，避免竞态条件导致匹配失败
            if (msg.id === messageId || (fallbackMessageId && msg.id === fallbackMessageId)) {
              let content = msg.content;
              if (status.status === 'success') {
                content = t('aiGenerate.imageGenerateDone');
              } else if (status.status === 'failed') {
                const friendlyError = extractFriendlyError(status.errorCode || status.errorMessage, t);
                content = t('aiGenerate.imageGenerateFailed', { message: friendlyError });
              }
              const generatedImages = status.images || msg.generatedImages;
              if (
                msg.id === messageId
                && msg.status === status.status
                && msg.content === content
                && msg.generatedImages === generatedImages
              ) {
                return msg;
              }
              changed = true;
              return {
                ...msg,
                id: messageId, // 确保 ID 更新为服务端返回的 ID
                status: status.status,
                content,
                generatedImages,
              };
            }
            return msg;
          });
          return changed ? next : prev;
        });

        if (status.status === 'success' || status.status === 'failed') {
          setGenerating(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          if (status.status === 'failed') {
            const friendlyError = extractFriendlyError(status.errorCode || status.errorMessage, t);
            message.error(friendlyError || t('aiGenerate.generateFailed'));
          }
        }
      } catch (error) {
        console.error('轮询状态失败:', error);
      }
    },
    [t]
  );

  // 发送生成请求
  const handleGenerate = async () => {
    if (!capability?.enabled) {
      message.warning(t('aiGenerate.featureUnavailable'));
      return;
    }
    if (!capability.eligible) {
      message.warning(t('aiGenerate.vipRequired'));
      return;
    }
    if (!inputText.trim()) {
      message.warning(t('aiGenerate.inputPromptRequired'));
      return;
    }

    const plannedOutputs = Math.max(1, inputImages.length) * generateParams.count;
    if (plannedOutputs > capability.maxOutputsPerTask) {
      message.warning(t('aiGenerate.maxOutputs', { count: capability.maxOutputsPerTask }));
      return;
    }
    if (plannedOutputs > capability.remaining) {
      message.warning(t('aiGenerate.insufficientQuota'));
      return;
    }

    let conversationId = currentConversationId;

    if (!conversationId) {
      try {
        const newConversation = await aiService.createConversation(
          inputText.slice(0, 20) || t('aiGenerate.newConversation')
        );
        setConversations([newConversation, ...conversations]);
        conversationId = newConversation.id;
        // 标记跳过下一次 loadMessages，避免覆盖即将添加的本地消息
        skipLoadMessagesRef.current = true;
        setCurrentConversationId(conversationId);
      } catch (error: any) {
        message.error(error.message || t('aiGenerate.createConversationFailed'));
        return;
      }
    }

    // 添加用户消息
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      conversationId: conversationId,
      role: 'user',
      content: inputText,
      images: inputImages.length > 0 ? inputImages.map((image) => image.url) : undefined,
      params: { ...generateParams },
      createdAt: new Date().toISOString(),
    };

    // 计算总生成数量：每张输入图片生成 count 张，如果没有输入图片则生成 count 张
    const inputCount = inputImages.length || 1;
    const totalCount = inputCount * generateParams.count;
    
    // 生成占位图片，关联输入图片
    const placeholderImages = [];
    for (let i = 0; i < inputCount; i++) {
      for (let j = 0; j < generateParams.count; j++) {
        placeholderImages.push({
          id: `img-${Date.now()}-${i}-${j}`,
          url: '',
          status: 'generating' as const,
          progress: 0,
          sourceImageUrl: inputImages.length > 0 ? inputImages[i].url : undefined,
          sourceRecordId: inputImages.length > 0 ? inputImages[i].id : undefined,
          requestedSize: generateParams.size,
          requestedQuality: generateParams.quality,
        });
      }
    }

    const aiMessage: Message = {
      id: `ai-${Date.now()}`,
      conversationId: conversationId,
      role: 'assistant',
      content: inputImages.length > 1
        ? t('aiGenerate.generatingMultiple', { count: inputImages.length, perCount: generateParams.count, total: totalCount })
        : t('aiGenerate.generatingSingle', { count: generateParams.count }),
      createdAt: new Date().toISOString(),
      status: 'generating',
      generatedImages: placeholderImages,
    };

    setMessages((prev) => [...prev, userMessage, aiMessage]);
    setInputText('');
    setInputImages([]);
    setGenerating(true);

    try {
      const response = await aiService.generateImage({
        conversationId: conversationId,
        prompt: inputText,
        inputImageRecordIds: inputImages.length > 0 ? inputImages.map((image) => image.id) : undefined,
        params: generateParams,
      });

      setCapability((current) => current
        ? { ...current, used: current.used + plannedOutputs, remaining: Math.max(0, current.remaining - plannedOutputs) }
        : current);

      // 更新 AI 消息 ID，同时保存原始 ID 用于兼容
      const originalMessageId = aiMessage.id;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === originalMessageId ? { ...msg, id: response.messageId } : msg
        )
      );

      // 开始轮询状态，同时匹配新旧两个 ID 以避免竞态条件
      pollingRef.current = setInterval(() => {
        pollTaskStatus(response.taskId, response.messageId, originalMessageId);
      }, 2000);
    } catch (error: any) {
      message.error(error.message || t('aiGenerate.generateRequestFailed'));
      setGenerating(false);
      // 移除生成中的消息
      setMessages((prev) => prev.filter((msg) => msg.id !== aiMessage.id));
    }
  };

  const handleImagesChange = (nextImages: InputImageRecord[]) => {
    const nextInputCount = Math.max(1, nextImages.length);
    const maxCount = Math.max(1, Math.floor((capability?.maxOutputsPerTask || 4) / nextInputCount));
    setInputImages(nextImages);
    setGenerateParams((current) => current.count > maxCount ? { ...current, count: maxCount } : current);
  };

  // 应用模板提示词
  const handleApplyTemplate = (template: EffectTemplate) => {
    setInputText(template.prompt);
  };

  // 清理轮询
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  return (
    <div className="ai-generate-page">
      {/* 左侧对话列表 */}
      <div className="ai-generate-sidebar">
        <ConversationList
          conversations={conversations}
          currentId={currentConversationId}
          loading={conversationsLoading}
          onSelect={handleSelectConversation}
          onCreate={handleCreateConversation}
          onDelete={handleDeleteConversation}
          onRename={handleRenameConversation}
        />
      </div>

      {/* 中间主区域 */}
      <div className="ai-generate-main">
        <ChatArea
          messages={messages}
          loading={messagesLoading}
          showCompare={showCompare}
        />
        <InputPanel
          images={inputImages}
          text={inputText}
          params={generateParams}
          capability={capability}
          generating={generating}
          onImagesChange={handleImagesChange}
          onTextChange={setInputText}
          onParamsChange={setGenerateParams}
          onGenerate={handleGenerate}
        />
      </div>

      {/* 右侧效果面板 */}
      <div className="ai-generate-effect">
        <EffectPanel
          templates={templates}
          showCompare={showCompare}
          onShowCompareChange={setShowCompare}
          onApplyTemplate={handleApplyTemplate}
        />
      </div>
    </div>
  );
};

export default AIGenerate;
